import { format } from 'date-fns'
import {
  ArrowLeft,
  BarChart3,
  Calendar as CalendarIcon,
  TrendingUp,
} from 'lucide-react'
import { useState } from 'react'

import { AddFilterButton, Filters } from '@/components'
import {
  Button,
  Calendar,
  Card,
  CardContent,
  CardHeading,
  ChartControls,
  ChartFilters,
  ChartTypeSwitcher,
  MultiLineChart,
  Popover,
  PopoverContent,
  PopoverTrigger,
  StackedBarChart,
} from '@/components/ui'
import type { DateRange as CalendarDateRange } from '@/components/ui/Calendar'
import type {
  ChartControlsState,
  MultiSeriesDataPoint,
} from '@/components/ui/chart/types'
import { filterByDateRange } from '@/lib/utils/filterByDateRange'
import {
  applyFilters,
  type FieldSchema,
  FiltersProvider,
  useFilters,
} from '@/stores'
import { parseCalendarDate, semanticColors } from '@/utils'
import type { MonthlyBreakdown } from '../hooks'

const INFLOW_OUTFLOW_SERIES = [
  { key: 'inflow', label: 'Inflow', color: semanticColors.inflow },
  { key: 'outflow', label: 'Outflow', color: semanticColors.outflow },
]

const CHART_TYPE_OPTIONS = [
  { value: 'bar', label: 'Bar', icon: BarChart3 },
  { value: 'line', label: 'Lines', icon: TrendingUp },
]

const CATEGORY_FILTER_FIELD = 'category'
const AMOUNT_FILTER_FIELD = 'amount'
const CATEGORY_FILTER_SCHEMA: FieldSchema[] = [
  {
    field: CATEGORY_FILTER_FIELD,
    label: 'Category',
    type: 'select',
    multiple: true,
  },
  {
    field: AMOUNT_FILTER_FIELD,
    label: 'Amount',
    type: 'number',
    formatter: (value: number) => `$${value.toFixed(2)}`,
  },
]

interface CategoryChartProps {
  title: string
  contextId: string
  monthlyByCategory: Record<string, MonthlyBreakdown[]>
  colorMap: Record<string, string>
  dateRange: CalendarDateRange | undefined
  onDateRangeChange: (range: CalendarDateRange | undefined) => void
  initialDate?: string
  finalDate?: string
  projectName?: string
  tickSpacing?: number
}

export function CategoryChart(props: CategoryChartProps) {
  return (
    <FiltersProvider contextId={props.contextId}>
      <CategoryChartContent {...props} />
    </FiltersProvider>
  )
}

function CategoryChartContent({
  title,
  contextId,
  monthlyByCategory,
  colorMap,
  dateRange,
  onDateRangeChange,
  initialDate,
  finalDate,
  projectName,
  tickSpacing,
}: CategoryChartProps) {
  const filters = useFilters()
  const categoryNames = Object.keys(monthlyByCategory)

  // Signed net per category so filters match the transactions table convention
  // (outflows are negative). `outflow` is stored as a positive magnitude in
  // useCategoryBreakdown, so we subtract it here.
  const filterRows = categoryNames.map((name) => ({
    [CATEGORY_FILTER_FIELD]: name,
    [AMOUNT_FILTER_FIELD]: monthlyByCategory[name].reduce(
      (sum, entry) => sum + entry.inflow - entry.outflow,
      0,
    ),
  }))

  const visibleCategories =
    filters.length === 0
      ? new Set(categoryNames)
      : new Set(
          applyFilters(filterRows, filters, CATEGORY_FILTER_SCHEMA).map(
            (row) => row[CATEGORY_FILTER_FIELD] as string,
          ),
        )

  const getUniqueValues = () => [...categoryNames].sort()
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null)
  const [chartType, setChartType] = useState<'bar' | 'line'>('bar')
  const [chartControls, setChartControls] = useState<ChartControlsState | null>(
    null,
  )

  const calendarSelection: CalendarDateRange | undefined = dateRange?.from
    ? dateRange
    : initialDate && finalDate
      ? {
          from: parseCalendarDate(initialDate),
          to: parseCalendarDate(finalDate),
        }
      : undefined

  const filteredMonthly: Record<string, MonthlyBreakdown[]> = {}
  const filterRange = dateRange?.from ? dateRange : 'all'
  for (const [categoryName, entries] of Object.entries(monthlyByCategory)) {
    if (!visibleCategories.has(categoryName)) continue
    const filtered = filterByDateRange(entries, filterRange)
    if (filtered.length > 0) {
      filteredMonthly[categoryName] = filtered
    }
  }

  const filteredTotals = Object.entries(filteredMonthly)
    .map(([categoryName, entries]) => {
      let inflow = 0
      let outflow = 0
      for (const entry of entries) {
        inflow += entry.inflow
        outflow += entry.outflow
      }
      return { date: categoryName, inflow, outflow }
    })
    .sort(
      (first, second) =>
        second.inflow + second.outflow - (first.inflow + first.outflow),
    )

  const lineChartDateMap = new Map<string, MultiSeriesDataPoint>()
  for (const categoryName of Object.keys(filteredMonthly)) {
    for (const monthEntry of filteredMonthly[categoryName]) {
      const existing = lineChartDateMap.get(monthEntry.date) || {
        date: monthEntry.date,
      }
      existing[categoryName] = monthEntry.inflow + monthEntry.outflow
      lineChartDateMap.set(monthEntry.date, existing)
    }
  }
  const lineChartData = Array.from(lineChartDateMap.values()).sort(
    (first, second) =>
      parseCalendarDate(first.date).getTime() -
      parseCalendarDate(second.date).getTime(),
  )

  const lineChartSeries = Object.keys(filteredMonthly).map((categoryName) => ({
    key: categoryName,
    label: categoryName,
    color: colorMap[categoryName] || colorMap[Object.keys(colorMap)[0]],
    area: false,
  }))

  const drillDownData =
    selectedCategory && filteredMonthly[selectedCategory]
      ? filteredMonthly[selectedCategory]
      : []

  const handleGroupClick = (data: MultiSeriesDataPoint) => {
    setSelectedCategory(data.date)
  }

  const handleBack = () => {
    setSelectedCategory(null)
  }

  const chartTitle = selectedCategory
    ? `${selectedCategory} monthly trend`
    : title

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-4">
        <Filters
          dialogId={contextId}
          schema={CATEGORY_FILTER_SCHEMA}
          getUniqueValues={getUniqueValues}
        />
        <ChartFilters>
          {!selectedCategory && (
            <>
              <ChartTypeSwitcher
                value={chartType}
                onValueChange={(value) => setChartType(value as 'bar' | 'line')}
                options={CHART_TYPE_OPTIONS}
              />
              <AddFilterButton
                dialogId={contextId}
                schema={CATEGORY_FILTER_SCHEMA}
              />
            </>
          )}
          <Popover>
            <PopoverTrigger asChild>
              <Button
                theme="outline"
                size="sm"
                className="h-8 gap-1.5 text-xs font-medium"
              >
                <CalendarIcon className="h-3.5 w-3.5 text-muted-foreground" />
                {dateRange?.from
                  ? dateRange.to
                    ? `${format(dateRange.from, 'MMM dd, yyyy')} - ${format(dateRange.to, 'MMM dd, yyyy')}`
                    : format(dateRange.from, 'MMM dd, yyyy')
                  : 'All time'}
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-auto p-0">
              <div className="flex flex-col sm:flex-row">
                <Calendar
                  mode="range"
                  selected={calendarSelection}
                  onSelect={(value) => {
                    if (
                      !value ||
                      (typeof value === 'object' && 'from' in value)
                    ) {
                      onDateRangeChange(value as CalendarDateRange | undefined)
                    }
                  }}
                  defaultMonth={
                    initialDate ? parseCalendarDate(initialDate) : undefined
                  }
                  numberOfMonths={1}
                  disabled={{
                    ...(initialDate
                      ? { before: parseCalendarDate(initialDate) }
                      : {}),
                    ...(finalDate
                      ? { after: parseCalendarDate(finalDate) }
                      : {}),
                  }}
                />
                <Calendar
                  mode="range"
                  selected={calendarSelection}
                  onSelect={(value) => {
                    if (
                      !value ||
                      (typeof value === 'object' && 'from' in value)
                    ) {
                      onDateRangeChange(value as CalendarDateRange | undefined)
                    }
                  }}
                  defaultMonth={
                    finalDate ? parseCalendarDate(finalDate) : undefined
                  }
                  numberOfMonths={1}
                  disabled={{
                    ...(initialDate
                      ? { before: parseCalendarDate(initialDate) }
                      : {}),
                    ...(finalDate
                      ? { after: parseCalendarDate(finalDate) }
                      : {}),
                  }}
                />
              </div>
              {dateRange?.from && (
                <div className="border-t px-3 py-2">
                  <Button
                    theme="ghost"
                    size="sm"
                    className="w-full text-xs"
                    onClick={() => onDateRangeChange(undefined)}
                  >
                    Reset to all time
                  </Button>
                </div>
              )}
            </PopoverContent>
          </Popover>
          {chartControls && (
            <ChartControls
              containerRef={chartControls.containerRef}
              isFullscreen={chartControls.isFullscreen}
              toggleFullscreen={chartControls.toggleFullscreen}
              fullscreenError={chartControls.fullscreenError}
              fullscreenSupported={chartControls.fullscreenSupported}
              filename={`${projectName || 'project'}-${chartTitle.toLowerCase().replace(/\s+/g, '-')}`}
              projectName={projectName}
              title={chartTitle}
              size="md"
              floating={false}
            />
          )}
        </ChartFilters>
      </div>

      <Card className="w-full">
        <CardHeading
          title={chartTitle}
          actions={
            <Button
              theme="outline"
              size="sm"
              onClick={handleBack}
              className={selectedCategory ? '' : 'invisible'}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Categories
            </Button>
          }
        />
        <CardContent>
          <div className="h-[400px] w-full">
            {filteredTotals.length === 0 && !selectedCategory ? (
              <div className="flex items-center justify-center h-full text-muted-foreground text-sm">
                No data in this date range
              </div>
            ) : selectedCategory ? (
              <MultiLineChart
                data={drillDownData}
                series={INFLOW_OUTFLOW_SERIES}
                height={400}
                marginTop={20}
                marginRight={30}
                marginBottom={40}
                marginLeft={70}
                showLegend
                legendPosition="top"
                onChartReady={setChartControls}
              />
            ) : chartType === 'bar' ? (
              <StackedBarChart
                data={filteredTotals}
                series={INFLOW_OUTFLOW_SERIES}
                height={400}
                marginTop={20}
                marginRight={30}
                marginBottom={80}
                marginLeft={70}
                onBarClick={handleGroupClick}
                rotateXLabels
                truncateXLabels
                maxLabelLength={15}
                tickSpacing={tickSpacing}
                showLegend
                legendPosition="top"
                onChartReady={setChartControls}
              />
            ) : (
              <MultiLineChart
                data={lineChartData}
                series={lineChartSeries}
                height={400}
                marginTop={20}
                marginRight={30}
                marginBottom={40}
                marginLeft={70}
                showLegend
                legendPosition="right"
                interactiveLegend
                onChartReady={setChartControls}
              />
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
